import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpHandler, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { storageDict } from '../services/storage/storage-dict';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { IdbStorageService } from '../services/storage/idb-storage.service';
import { AlertModalService } from '../services/alert-modal.service';
import { ClinicEnvironmentService } from '../services/clinic-environment.service';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private alertService: AlertModalService,
    private authService: AuthService,
    private clinicEnv: ClinicEnvironmentService,
    private storage: IdbStorageService,
    private router: Router
  ) {
    this.logOut = this.logOut.bind(this);
  }

  async logOut() {
    return this.authService.logoutGracefully();
  }

  setHeaders(req: HttpRequest<any>): (obj: { token: string }) => HttpRequest<any> {
    return (obj: { token: string }) => {
      let token: string;
      if (obj !== null) {
        token = typeof obj === 'string' ? obj : obj.token;
      }

      const defaultHeaders = {
        accept: 'application/json',
        'app-source': 'mobile-klinik',
      };

      const setHeaders = token
        ? { ...defaultHeaders, Authorization: `Bearer ${token}` }
        : defaultHeaders;

      return req.clone({
        setHeaders,
      });
    };
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    /** Whitelisted route, will not need to have auth headers. */
    const whitelist = [
      '/sign-in',
      '/sign-up',
      '/sign-out',
      '/klinik/auth',
      '/data-klinik/url',
      'api.ipify.org',
      'office.com',
      'amazonaws.com',
      'placeholder.com',
      'hooks.slack.com',
      'bucket.periksa.net',
      'apijkn-dev.bpjs-kesehatan.go.id',
      'apijkn.bpjs-kesehatan.go.id',
      'n8n.periqtsa.com',
      'ocr.periksaapis.com',
    ];

    if (new RegExp(whitelist.join('|')).test(req.url.toLowerCase())) {
      return next.handle(req);
    }

    // Basic authentication required for BFF.
    // if (req.url.includes(environment.urlBff)) {
    //   const basicAuth = btoa(`${environment.apiId}:${environment.bffApiPass}`);
    //   const bffRequest = req.clone({
    //     headers: req.headers
    //       .set('accept', 'application/json')
    //       .set('Authorization', `Basic ${basicAuth}`),
    //   });
    //   return next.handle(bffRequest);
    // }

    /** Handle null data-klinik, can be occured when it is not loaded. */
    if (
      req.url.toLowerCase().includes('/data-klinik/null') ||
      req.url.toLowerCase().includes('/data-klinik/undefined') ||
      req.url.toLowerCase().includes('/clinics/null') ||
      req.url.toLowerCase().includes('/clinics/undefined')
    ) {
      console.warn(
        `WARN: data-klinik is invalid. Service state: ${this.clinicEnv.clinicId}. URL: ${req.url}`
      );
      this.logOut();
      return next.handle(req);
    }

    return this.storage.getItemSync(storageDict.authLocalStorageToken).pipe(
      map(this.setHeaders(req)),
      switchMap((parsedRequest: any) => next.handle(parsedRequest)),
      catchError((err) => {
        // console.log(err, this.authService.authState$.value);
        /**
         * ? If request is unauthorized (401) or forbidden, it is possible that our token is expired or not valid.
         * ? To resolve the issue, we ask a new token using available refresh token from the db.
         * ? It will try *once* again, and if the response is valid, it will save to the storage.
         */
        if (err.status === 403 && this.authService.authState$.value) {
          return this.authService.getNewToken().pipe(
            tap((newToken) =>
              this.storage.setItem(storageDict.authLocalStorageToken, newToken.token)
            ),
            map(this.setHeaders(req)),
            switchMap((newRequest) => next.handle(newRequest)),
            catchError((retryError) => {
              if (retryError.url.includes('backend-web-klinik')) return throwError(retryError);
              if (retryError.status === 401 || retryError.status === 403) {
                this.alertService.showInfoAlert(
                  'Sesi Anda sudah habis. Silakan login kembali.',
                  'Mohon Maaf',
                  'Ok'
                );
                this.logOut();
              }
              return throwError(retryError);
            })
          );
        }
        return throwError(err);
      })
    );
  }
}
